/* Modal CRM para registrar feedback tras visita (equivalente al cierre en móvil). */

import { useState, useEffect, useCallback } from "react";
import api from "../services/api";
import { dialogs } from "../context/DialogContext";
import { VISITA_RESULTADO_GRUPOS, resultadosPorGrupo } from "../constants/visitaResultados";
import usePermiso from "../hooks/usePermiso";

/** Permiso granular (t_m2_*) de cada opción de resultado de visita, por valor o por grupo. */
function m2OpcionNodeId(grupoId, valor) {
  switch (valor) {
    case "Reservado":
      return "t_m2_reservado";
    case "Muy interesado":
      return "t_m2_muy_interesado";
    case "Segunda visita":
      return "t_m2_segunda_visita";
    case "Pasa oferta":
      return "t_m2_pasa_oferta";
    default:
      break;
  }
  if (grupoId === "objeciones") return "t_m2_objeciones";
  if (grupoId === "descarte") return "t_m2_descarte";
  return null;
}

function formatRango(fechaInicio, fechaFin) {
  const fmt = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString("es-ES", {
      weekday: "short",
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Europe/Madrid",
    });
  };
  const a = fmt(fechaInicio);
  const b = fmt(fechaFin);
  if (a && b) return `${a} — ${b}`;
  return a || b || "";
}

export default function ModalFeedbackVisita({
  isOpen,
  onClose,
  agendaId,
  theme,
  onCompleted,
}) {
  const c = theme?.colors || {};
  const isDark = theme?.name === "dark";
  const { puede } = usePermiso();
  const puedeVerObservaciones = puede("t_m2_observaciones", "ver");
  const puedeModificarObservaciones = puede("t_m2_observaciones", "modificar");
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [detalle, setDetalle] = useState(null);
  const [seleccion, setSeleccion] = useState({});
  const [observaciones, setObservaciones] = useState({});

  const cargar = useCallback(async () => {
    if (!agendaId) return;
    setCargando(true);
    try {
      const res = await api.get(`/agenda/${agendaId}/detalle-movil`);
      const payload = res.data?.data ?? res.data ?? res;
      setDetalle(payload);
      if (payload?.metadata_movil?.visita_finalizada_at) {
        await onCompleted?.(payload);
        onClose?.();
      }
    } catch (e) {
      await dialogs.alert(e.response?.data?.error || e.message || "No se pudo cargar la cita");
      onClose?.();
    } finally {
      setCargando(false);
    }
  }, [agendaId, onClose, onCompleted]);

  useEffect(() => {
    if (!isOpen || !agendaId) {
      setDetalle(null);
      setSeleccion({});
      setObservaciones({});
      return;
    }
    cargar();
  }, [isOpen, agendaId, cargar]);

  const props = detalle?.propiedades || [];
  const cita = detalle?.cita || {};
  const cliente = detalle?.cliente_vista || {};

  const handleGuardar = async () => {
    if (!agendaId || guardando) return;
    if (!props.length) {
      await dialogs.alert("Esta cita no tiene propiedades vinculadas.");
      return;
    }
    const calificaciones = [];
    for (const p of props) {
      const pid = String(p.id);
      const sel = seleccion[pid];
      if (!sel?.valor) {
        await dialogs.alert(`Selecciona un resultado para Ref. ${p.referencia || pid}`);
        return;
      }
      calificaciones.push({
        propiedad_id: pid,
        calificacion: sel.valor,
        grupo: sel.grupo,
        observaciones: observaciones[pid] || "",
      });
    }
    setGuardando(true);
    try {
      const res = await api.post(`/agenda/${agendaId}/finalizar-visita-movil`, { calificaciones });
      await onCompleted?.(res.data || res);
      onClose?.();
    } catch (e) {
      await dialogs.alert(e.response?.data?.error || e.message || "No se pudo guardar el feedback");
    } finally {
      setGuardando(false);
    }
  };

  if (!isOpen || !agendaId) return null;

  const overlay = {
    position: "fixed",
    inset: 0,
    zIndex: 1300,
    background: "rgba(0,0,0,0.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  };

  const panel = {
    background: c.bgPanel || "#fff",
    borderRadius: 14,
    padding: 24,
    width: "100%",
    maxWidth: 640,
    maxHeight: "90vh",
    overflow: "auto",
    border: `1px solid ${c.border || "#e2e8f0"}`,
    boxShadow: isDark ? "0 20px 50px rgba(0,0,0,0.5)" : "0 20px 40px rgba(15,23,42,0.15)",
  };

  return (
    <div style={overlay} onClick={() => !guardando && onClose?.()}>
      <div style={panel} onClick={(e) => e.stopPropagation()}>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: c.textMuted, marginBottom: 6 }}>
            Feedback tras visita
          </div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: c.textPrimary }}>
            {cita.titulo || "Cita"}
          </h2>
          {formatRango(cita.fecha_inicio, cita.fecha_fin) && (
            <p style={{ margin: "8px 0 0", fontSize: 13, color: c.textSecondary }}>{formatRango(cita.fecha_inicio, cita.fecha_fin)}</p>
          )}
          {cliente.nombre && (
            <p style={{ margin: "6px 0 0", fontSize: 13, color: c.textSecondary }}>
              Cliente: <strong>{cliente.nombre}</strong>
              {cliente.telefono ? ` · ${cliente.telefono}` : ""}
            </p>
          )}
        </div>

        {cargando && (
          <div style={{ fontSize: 13, color: c.textMuted, textAlign: "center", padding: 24 }}>Cargando cita…</div>
        )}

        {!cargando && props.map((p) => {
          const pid = String(p.id);
          const sel = seleccion[pid];
          return (
            <div
              key={pid}
              style={{
                marginBottom: 16,
                padding: 14,
                borderRadius: 10,
                border: `1px solid ${c.border || "#e2e8f0"}`,
                background: isDark ? "rgba(255,255,255,0.03)" : "#f8fafc",
              }}
            >
              <div style={{ fontSize: 14, fontWeight: 800, color: c.textPrimary, marginBottom: 4 }}>
                Ref. {p.referencia || pid}
              </div>
              <div style={{ fontSize: 12, color: c.textMuted, marginBottom: 10 }}>
                {[p.tipo_nombre, p.poblacion_nombre].filter(Boolean).join(" · ")}
              </div>

              {VISITA_RESULTADO_GRUPOS.map((gr) => {
                // Cada opción es un nodo t_m2_* independiente: se oculta si no tiene "ver"
                // (por defecto todos son permisivos, sin cambio de comportamiento).
                const opciones = resultadosPorGrupo(gr.id).filter((op) => puede(m2OpcionNodeId(gr.id, op.valor), "ver"));
                if (!opciones.length) return null;
                return (
                  <div key={gr.id} style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: c.textMuted, marginBottom: 4 }}>{gr.label}</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {opciones.map((op) => {
                        const activo = sel?.valor === op.valor;
                        return (
                          <button
                            key={op.valor}
                            type="button"
                            disabled={guardando}
                            onClick={() => setSeleccion((prev) => ({ ...prev, [pid]: { valor: op.valor, grupo: op.grupo, score: op.score } }))}
                            style={{
                              padding: "6px 10px",
                              borderRadius: 8,
                              border: activo ? "2px solid #16a34a" : `1px solid ${c.border}`,
                              background: activo ? (isDark ? "#16a34a22" : "#dcfce7") : "transparent",
                              color: c.textPrimary,
                              fontSize: 11,
                              fontWeight: activo ? 700 : 500,
                              cursor: "pointer",
                              fontFamily: "inherit",
                            }}
                          >
                            {op.valor}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              {puedeVerObservaciones && (
                <>
                  <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: c.textMuted, marginTop: 8, marginBottom: 4 }}>
                    Observaciones
                  </label>
                  <textarea
                    value={observaciones[pid] || ""}
                    onChange={(e) => setObservaciones((prev) => ({ ...prev, [pid]: e.target.value }))}
                    rows={2}
                    maxLength={2500}
                    disabled={guardando || !puedeModificarObservaciones}
                    title={!puedeModificarObservaciones ? "No tienes permiso para modificar las observaciones" : undefined}
                    placeholder="Notas sobre el interés del cliente en esta vivienda…"
                    style={{
                      width: "100%",
                      boxSizing: "border-box",
                      borderRadius: 8,
                      border: `1px solid ${c.border}`,
                      background: c.bgInput,
                      color: c.textPrimary,
                      padding: "8px 10px",
                      fontSize: 12,
                      fontFamily: "inherit",
                      resize: "vertical",
                      opacity: puedeModificarObservaciones ? 1 : 0.6,
                      cursor: puedeModificarObservaciones ? "text" : "not-allowed",
                    }}
                  />
                </>
              )}
            </div>
          );
        })}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
          <button
            type="button"
            disabled={guardando}
            onClick={onClose}
            style={{
              padding: "8px 14px",
              borderRadius: 8,
              border: `1px solid ${c.border}`,
              background: "transparent",
              color: c.textSecondary,
              fontSize: 12,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            Ahora no
          </button>
          <button
            type="button"
            disabled={guardando || cargando || !props.length}
            onClick={handleGuardar}
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              border: "none",
              background: "#16a34a",
              color: "#fff",
              fontSize: 12,
              fontWeight: 700,
              cursor: guardando ? "wait" : "pointer",
              fontFamily: "inherit",
              opacity: guardando ? 0.7 : 1,
            }}
          >
            {guardando ? "Guardando…" : "Guardar feedback"}
          </button>
        </div>
      </div>
    </div>
  );
}
